import { DetectedKey } from "./key";
import { MusicalTimeline } from "./musical-timeline";
import { Part } from "./part";
import type {
  AnalysisKey,
  AnalysisChord,
  AnalysisRun,
  AnalysisSection,
  AnalysisPhrases,
  AnalysisStatistics,
  Difficulty,
  AnalysisHandGrips,
  AnalysisHandPlacements,
} from "./analyzer-types";

// ── Analysis data (populated from API, absent for live/local parsing) ──

export type SongAnalysis = {
  key: AnalysisKey;
  chords: AnalysisChord[];
  sections: AnalysisSection[];
  /** Le découpage au-dessus des sections, quand il y en a un. Voir `AnalysisRun`. */
  runs?: AnalysisRun[];
  phrases: AnalysisPhrases;
  /** Absent from analyses stored before the analyzer decided it. */
  handPlacements?: AnalysisHandPlacements;
  handGrips?: AnalysisHandGrips;
  statistics: AnalysisStatistics;
};

/**
 * Un changement d'état de la pédale forte, au tick où il se produit. Voir
 * `Song.getSustain`.
 */
export type SustainChange = {
  tick: number;
  down: boolean;
};

export class Song {
  private musicalTimeline: MusicalTimeline;
  private parts: Part[] = [];
  private key: DetectedKey | null = null;
  private sustain: readonly SustainChange[] = [];

  /** Rich analysis data from the API. Null when Song is built locally (e.g. parseMidi). */
  private analysis: SongAnalysis | null = null;

  private title: string | null = null;
  private composer: string | null = null;
  private context: string | null = null;
  private difficulty: Difficulty | null = null;

  constructor(
    private ppq: number,
    private durationTicks: number,
  ) {
    this.musicalTimeline = new MusicalTimeline(ppq);
    this.durationTicks = durationTicks;
  }

  setAnalysis(analysis: SongAnalysis): void {
    this.analysis = analysis;
  }

  getAnalysis(): SongAnalysis | null {
    return this.analysis;
  }

  getSections(): AnalysisSection[] {
    return this.analysis?.sections ?? [];
  }

  /**
   * Le découpage au-dessus des sections — les prises d'un enregistrement, par
   * exemple. Vide pour un morceau ordinaire, qui n'en a pas.
   */
  getRuns(): AnalysisRun[] {
    return this.analysis?.runs ?? [];
  }

  getPhrases(): AnalysisPhrases | null {
    return this.analysis?.phrases ?? null;
  }

  /**
   * Where each hand sits over the whole piece, or `null` on an analysis
   * produced before the analyzer emitted it. Consumers must handle the null:
   * re-deriving the segmentation locally is what this field exists to stop.
   */
  getHandPlacements(): AnalysisHandPlacements | null {
    return this.analysis?.handPlacements ?? null;
  }

  /**
   * Where each hand *is* over the whole piece, or `null` when the analysis
   * ran without fingering or predates the field. Consumers must handle the
   * null: re-deriving the segmentation locally is what this field exists to
   * stop.
   */
  getHandGrips(): AnalysisHandGrips | null {
    return this.analysis?.handGrips ?? null;
  }

  /** Ticks at which a chord starts, ascending. */
  getChordStartTicks(): readonly number[] {
    return this.getAnalysisChords().map((chord) => chord.tick);
  }

  getStatistics(): AnalysisStatistics | null {
    return this.analysis?.statistics ?? null;
  }

  getAnalysisChords(): AnalysisChord[] {
    return this.analysis?.chords ?? [];
  }

  getMusicalTimeline(): MusicalTimeline {
    return this.musicalTimeline;
  }

  /**
   * Le pédalage du morceau : les moments où l'étouffoir se lève et ceux où il
   * retombe, en ordre de tick.
   *
   * Sur le morceau et non sur ses parties, parce qu'un piano n'a qu'un jeu
   * d'étouffoirs : la pédale tient les deux mains ensemble, et c'est à peu
   * près tout ce qu'elle fait. La source le dit de la même façon — dans sa
   * carte des temps, et non par piste.
   *
   * Vide quand elle n'a rien écrit, ce qui est le cas courant. Ça ne veut pas
   * dire que le morceau se joue sans pédale : seulement que personne ne l'a
   * notée, et qu'on n'ira pas l'inventer.
   */
  getSustain(): readonly SustainChange[] {
    return this.sustain;
  }

  setSustain(sustain: readonly SustainChange[]): void {
    this.sustain = sustain;
  }

  addPart(part: Part) {
    this.parts.push(part);
  }

  getParts(): Part[] {
    return this.parts;
  }

  getTotalBars(): number {
    return this.musicalTimeline.getBarCount(this.durationTicks);
  }

  getDurationTicks(): number {
    return this.durationTicks;
  }

  getBars() {
    return this.musicalTimeline.getBars(this.durationTicks);
  }

  getBar(tick: number) {
    return this.musicalTimeline.getBar(tick, this.durationTicks);
  }

  getKey(): DetectedKey | null {
    return this.key;
  }

  setKey(key: DetectedKey | null): void {
    this.key = key;
  }

  // ── Metadata ─────────────────────────────────────────────────

  getTitle(): string | null {
    return this.title;
  }

  setTitle(title: string | null): void {
    this.title = title;
  }

  getComposer(): string | null {
    return this.composer;
  }

  setComposer(composer: string | null): void {
    this.composer = composer;
  }

  /**
   * Broader work the piece belongs to, sourced from the title normalizer
   * (`InspectResult.suggestedContext`). Format is a ready-to-display
   * string (e.g. `"BO de Clair Obscur: Expedition 33 (2025)"`).
   */
  getContext(): string | null {
    return this.context;
  }

  setContext(context: string | null): void {
    this.context = context;
  }

  getDifficulty(): Difficulty | null {
    return this.difficulty;
  }

  setDifficulty(difficulty: Difficulty | null): void {
    this.difficulty = difficulty;
  }

}
